import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_PLAYER_NAMES, DEFAULT_ZAYAC_SETTINGS, ZAYAC_MAX_PLAYERS, ZAYAC_MIN_PLAYERS, type TeamSummary, type ZayacGameSettings } from '@shum/shared';
import { api } from '../lib/api';
import { GameLayout } from './GameLayout';

export function CreateZayacRoom() {
  const navigate = useNavigate();
  const [names, setNames] = useState<string[]>([...DEFAULT_PLAYER_NAMES]);
  const [hostIndex, setHostIndex] = useState(0);
  const [draft, setDraft] = useState('');
  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [teamName, setTeamName] = useState('');
  const [settings, setSettings] = useState<ZayacGameSettings>({ ...DEFAULT_ZAYAC_SETTINGS });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    api.listTeams().then(setTeams).catch(() => {});
  }, []);

  const count = names.filter((n) => n.trim()).length;
  const hostName = names[hostIndex]?.trim() ?? '';
  const validation = countValidation(count);

  function addPlayer() {
    const n = draft.trim();
    if (!n) return;
    if (names.some((x) => x.toLowerCase() === n.toLowerCase())) {
      setError('Такое имя уже есть.');
      return;
    }
    setNames((p) => [...p, n]);
    setDraft('');
    setError(null);
  }

  function removeAt(i: number) {
    setNames((p) => p.filter((_, idx) => idx !== i));
    setHostIndex((current) => {
      if (i < current) return current - 1;
      if (i === current) return Math.max(0, Math.min(current, names.length - 2));
      return current;
    });
  }

  function renameAt(i: number, value: string) {
    setNames((p) => p.map((x, idx) => (idx === i ? value : x)));
  }

  function loadTeam(id: string) {
    if (!id) return;
    api
      .getTeam(id)
      .then((t) => {
        setNames(t.players.map((p) => p.name));
        setHostIndex(0);
        setTeamName(t.name);
        setMsg(`Загружена команда «${t.name}»`);
      })
      .catch(() => setError('Не удалось загрузить команду.'));
  }

  async function saveTeam() {
    if (!teamName.trim()) {
      setError('Введите название команды.');
      return;
    }
    try {
      await api.createTeam(teamName.trim(), names);
      setTeams(await api.listTeams());
      setMsg('Команда сохранена.');
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function create() {
    if (!validation.ok) {
      setError(validation.message);
      return;
    }
    if (!hostName) {
      setError('Отметьте игрока, который будет ведущим.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.createZayacRoom({
        players: names.filter((n) => n.trim()),
        creatorName: hostName,
        settings,
      });
      navigate(`/zayac/${res.code}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <GameLayout>
      <CreateStyles />
      <span className="zy-label">/ Заяц Live · создание комнаты</span>
      <h1 className="zy-cr-title">Кто сегодня играет?</h1>
      <p className="zy-lede">
        Введите имена всех игроков заранее — от {ZAYAC_MIN_PLAYERS} до {ZAYAC_MAX_PLAYERS}. Никаких прозвищ: каждый выберет своё имя из списка.
      </p>

      <div className="zy-cr-layout">
        <div className="zy-cr-main">
          <div className="zy-panel">
            <div className="zy-panel-head">
              <h2>Состав</h2>
              <span className="zy-pill">{count} игроков</span>
            </div>

            <div className="zy-team-row">
              <input
                className="zy-text-input"
                placeholder="Название команды (необязательно)"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
              />
            </div>

            {teams.length > 0 && (
              <div className="zy-team-row">
                <select className="zy-text-input" value="" onChange={(e) => loadTeam(e.target.value)}>
                  <option value="">Загрузить сохранённую команду…</option>
                  {teams.map((t) => (
                    <option key={t.teamId} value={t.teamId}>
                      {t.name} · {t.playerCount} игр.
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="zy-players">
              <p className="zy-host-note">Отметьте одного игрока как ведущего — он будет запускать игру, переключать говорящих и завершать голосование.</p>
              {names.map((n, i) => (
                <div className="zy-player-row" key={i}>
                  <span className="zy-pidx mono">{(i + 1).toString().padStart(2, '0')}</span>
                  <input
                    className="zy-text-input"
                    value={n}
                    onChange={(e) => renameAt(i, e.target.value)}
                    placeholder={`Игрок ${i + 1}`}
                  />
                  <label className="zy-host-check" title="Назначить ведущим">
                    <input type="checkbox" checked={hostIndex === i} onChange={() => setHostIndex(i)} />
                    <span>Ведущий</span>
                  </label>
                  <button className="zy-icon-btn" onClick={() => removeAt(i)} aria-label="Удалить" disabled={names.length <= 1}>
                    ×
                  </button>
                </div>
              ))}
            </div>

            <form
              className="zy-add-row"
              onSubmit={(e) => {
                e.preventDefault();
                addPlayer();
              }}
            >
              <input
                className="zy-text-input"
                placeholder="Имя нового игрока"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <button type="submit" className="btn-ghost" disabled={names.length >= ZAYAC_MAX_PLAYERS}>
                Добавить
              </button>
            </form>

            <div className="zy-cr-actions">
              <button className="btn-ghost" onClick={saveTeam} disabled={!count}>
                Сохранить команду
              </button>
              <button className="btn-primary" onClick={create} disabled={!validation.ok || !hostName || busy}>
                {busy ? 'Создаём…' : 'Создать комнату →'}
              </button>
            </div>
            {!validation.ok && <div className="zy-warn">{validation.message}</div>}
            {!hostName && <div className="zy-warn">Выберите ведущего из заполненных имён.</div>}
            {error && <div className="zy-warn zy-warn-err">{error}</div>}
            {msg && <div className="zy-warn zy-warn-ok">{msg}</div>}
          </div>
        </div>

        <aside className="zy-cr-side">
          <div className="zy-panel">
            <div className="zy-panel-head">
              <h2>Правила</h2>
              <span className="zy-label">режим «Заяц»</span>
            </div>
            <p className="zy-hint">
              Каждый раунд все, кроме одного, получают секретное слово. Этот один — Заяц. Все говорят по ассоциации,
              потом голосуют, кто Заяц. Поймали — +1 мирным. Не поймали — +2 зайцу.
            </p>
            <Slider
              label="Раундов"
              value={settings.rounds}
              min={1}
              max={15}
              step={1}
              onChange={(v) => setSettings((s) => ({ ...s, rounds: v }))}
            />
            <label className="zy-check">
              <input
                type="checkbox"
                checked={settings.avoidRepeatZayac}
                onChange={(e) => setSettings((s) => ({ ...s, avoidRepeatZayac: e.target.checked }))}
              />
              Не выбирать Зайца дважды подряд
            </label>
            <label className="zy-check">
              <input
                type="checkbox"
                checked={!settings.selfVotingAllowed}
                onChange={(e) => setSettings((s) => ({ ...s, selfVotingAllowed: !e.target.checked }))}
              />
              Запретить голосовать за себя
            </label>
            <label className="zy-check">
              <input
                type="checkbox"
                checked={settings.manualVoting}
                onChange={(e) => setSettings((s) => ({ ...s, manualVoting: e.target.checked }))}
              />
              Объединить обсуждение с голосованием
            </label>
          </div>

          <div className="zy-panel">
            <div className="zy-panel-head">
              <h2>Тайминги</h2>
            </div>
            <Slider
              label="На ассоциацию · ориентир"
              value={settings.turnSeconds}
              min={5}
              max={60}
              step={5}
              onChange={(v) => setSettings((s) => ({ ...s, turnSeconds: v }))}
            />
            <Slider
              label="Обсуждение"
              value={settings.discussionSeconds}
              min={0}
              max={300}
              step={15}
              onChange={(v) => setSettings((s) => ({ ...s, discussionSeconds: v }))}
            />
            <p className="zy-hint">Ассоциация не переключается автоматически: ведущий нажимает «Следующий игрок». Голосование идёт без таймера и тоже завершается ведущим.</p>
          </div>
        </aside>
      </div>
    </GameLayout>
  );
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="zy-slider">
      <div className="zy-slider-head">
        <span>{label}</span>
        <span className="mono">{formatSec(value)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}

function formatSec(v: number): string {
  if (v < 60) return `${v}с`;
  const m = Math.floor(v / 60);
  const s = v % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function countValidation(count: number): { ok: boolean; message: string } {
  if (count < ZAYAC_MIN_PLAYERS) return { ok: false, message: `Нужно минимум ${ZAYAC_MIN_PLAYERS} игрока. Сейчас ${count}.` };
  if (count > ZAYAC_MAX_PLAYERS) return { ok: false, message: `Максимум ${ZAYAC_MAX_PLAYERS} игроков. Сейчас ${count}.` };
  return { ok: true, message: '' };
}

function CreateStyles() {
  return (
    <style>{`
      .zy-cr-title { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 64px; line-height: 1.02; letter-spacing: -.015em; margin: 12px 0 0; }
      .zy-lede { max-width: 640px; font-size: 16px; line-height: 1.55; color: var(--ink-dim); margin: 20px 0 40px; }
      .zy-cr-layout { display: grid; grid-template-columns: 1.5fr 1fr; gap: 24px; align-items: start; }
      .zy-panel { background: var(--bg2); border: 1px solid var(--line); border-radius: var(--radius-card); padding: 24px; margin-bottom: 16px; }
      .zy-panel-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 16px; }
      .zy-panel-head h2 { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 28px; margin: 0; letter-spacing: -.005em; }
      .zy-team-row { margin-bottom: 12px; }
      .zy-text-input { width: 100%; background: var(--bg); border: 1px solid var(--line-strong); border-radius: var(--radius-btn); color: var(--ink); padding: 12px 14px; font-family: 'Space Grotesk', sans-serif; font-size: 15px; outline: none; }
      .zy-text-input:focus { border-color: var(--accent); }
      select.zy-text-input { appearance: none; cursor: pointer; }
      .zy-players { display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px; }
      .zy-player-row { display: grid; grid-template-columns: 32px 1fr auto 40px; gap: 10px; align-items: center; }
      .zy-pidx { font-size: 12px; color: var(--ink-faint); text-align: center; }
      .zy-host-note { margin: 0 0 2px; color: var(--ink-dim); font-size: 13px; line-height: 1.45; }
      .zy-host-check { display: inline-flex; align-items: center; gap: 6px; color: var(--ink-dim); font-size: 12px; white-space: nowrap; cursor: pointer; }
      .zy-host-check input { width: 16px; height: 16px; margin: 0; accent-color: var(--accent); cursor: pointer; }
      .zy-icon-btn { appearance: none; border: 1px solid var(--line); background: transparent; color: var(--ink-dim); border-radius: var(--radius-btn); font-size: 18px; cursor: pointer; height: 44px; transition: color .2s, border-color .2s; }
      .zy-icon-btn:hover:not(:disabled) { color: var(--danger); border-color: var(--danger); }
      .zy-icon-btn:disabled { opacity: .4; cursor: not-allowed; }
      .zy-add-row { display: grid; grid-template-columns: 1fr auto; gap: 10px; margin-bottom: 20px; }
      .zy-cr-actions { display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
      .zy-warn { font-size: 13px; color: var(--ink-dim); margin-top: 12px; }
      .zy-warn-err { color: var(--danger); }
      .zy-warn-ok { color: var(--accent); }
      .zy-hint { font-size: 13px; color: var(--ink-dim); line-height: 1.5; margin: 0 0 16px; }
      .zy-slider { margin-bottom: 16px; }
      .zy-slider-head { display: flex; justify-content: space-between; font-size: 14px; margin-bottom: 6px; color: var(--ink-dim); }
      .zy-slider input[type=range] { width: 100%; accent-color: var(--accent); }
      .zy-check { display: flex; align-items: center; gap: 10px; font-size: 14px; color: var(--ink-dim); cursor: pointer; margin-bottom: 8px; }
      .zy-check input { accent-color: var(--accent); width: 18px; height: 18px; }
      @media (max-width: 900px) { .zy-cr-layout { grid-template-columns: 1fr; } .zy-cr-title { font-size: 44px; } }
    `}</style>
  );
}
